import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarRange, CheckCircle2, Download, FileSpreadsheet, FileText, Loader2, Package, ShoppingCart } from "lucide-react";
import {
  downloadPurchaseReportExport,
  fetchPurchaseReport,
  type PurchaseReport,
  type PurchaseReportDateField,
  type PurchaseReportRequest,
} from "@/lib/adminPurchaseReportApi";
import { fetchAdminOrderSummaries } from "@/lib/ordersApi";
import type { OrderStatus, OrderSummary } from "@/lib/types";
import { hasPermission } from "@/lib/types";
import { useApp } from "@/store/useApp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type Mode = "day" | "range" | "orders";
type DetailMode = "none" | "customer" | "order";

const DETAIL_OPTIONS: ReadonlyArray<readonly [DetailMode, string]> = [
  ["none", "Consolidado"],
  ["customer", "Por cliente"],
  ["order", "Por pedido"],
];

const HANDLED_STATUSES: ReadonlySet<OrderStatus> = new Set([
  "EM_PRODUCAO",
  "EM_EXPEDICAO",
  "EM_ROTA",
  "ENTREGUE",
]);

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function fmtQty(value: number): string {
  if (!value) return "—";
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const STATUS_LABEL: Record<OrderStatus, string> = {
  CRIADO: "Criado",
  EM_COMPRA: "Em compra",
  EM_PRODUCAO: "Em produção",
  EM_EXPEDICAO: "Expedição",
  EM_ROTA: "Em entrega",
  ENTREGUE: "Entregue",
  CANCELADO: "Cancelado",
};

export default function AdminPurchaseListReport() {
  const canExport = hasPermission(useApp((s) => s.account), "RELATORIOS.LISTA_COMPRAS.EXPORTAR");
  const [mode, setMode] = useState<Mode>("day");
  const [day, setDay] = useState<string>(todayIso());
  const [from, setFrom] = useState<string>(todayIso());
  const [to, setTo] = useState<string>(todayIso());

  const [allOrders, setAllOrders] = useState<OrderSummary[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [orderQuery, setOrderQuery] = useState("");
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());

  const [report, setReport] = useState<PurchaseReport | null>(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exporting, setExporting] = useState<"pdf" | "xlsx" | null>(null);

  const [customerFilter, setCustomerFilter] = useState<string>("");
  const [detailMode, setDetailMode] = useState<DetailMode>("none");
  const [dateField, setDateField] = useState<PurchaseReportDateField>("DELIVERY_DATE");

  useEffect(() => {
    setLoadingOrders(true);
    fetchAdminOrderSummaries()
      .then((orders) => {
        const active = orders.filter((o) => o.status !== "CANCELADO" && o.status !== "CRIADO");
        setAllOrders(active);
      })
      .catch(() => toast.error("Erro ao carregar pedidos"))
      .finally(() => setLoadingOrders(false));
  }, []);

  const buildRequest = useCallback((): PurchaseReportRequest | null => {
    const extras = {
      customerId: customerFilter || null,
      groupByCustomer: detailMode === "customer",
      groupByOrder: detailMode === "order",
      dateField,
    };
    if (mode === "day") {
      if (!day) return null;
      return { from: day, to: day, ...extras };
    }
    if (mode === "range") {
      if (!from || !to) return null;
      if (from > to) {
        toast.error("Data inicial deve ser anterior à data final");
        return null;
      }
      return { from, to, ...extras };
    }
    if (selectedOrderIds.size === 0) {
      toast.error("Selecione ao menos um pedido");
      return null;
    }
    return { orderIds: Array.from(selectedOrderIds), ...extras };
  }, [mode, day, from, to, selectedOrderIds, customerFilter, detailMode, dateField]);

  const loadReport = useCallback(async () => {
    const request = buildRequest();
    if (!request) return;
    setLoadingReport(true);
    try {
      const result = await fetchPurchaseReport(request);
      setReport(result);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Erro ao gerar relatório";
      toast.error(msg);
    } finally {
      setLoadingReport(false);
    }
  }, [buildRequest]);

  const handleExport = useCallback(
    async (format: "pdf" | "xlsx") => {
      const request = buildRequest();
      if (!request) return;
      setExporting(format);
      try {
        const { filename, blob } = await downloadPurchaseReportExport(request, format);
        downloadBlob(filename, blob);
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Erro ao exportar arquivo";
        toast.error(msg);
      } finally {
        setExporting(null);
      }
    },
    [buildRequest],
  );

  const uniqueCustomers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const o of allOrders) {
      if (!seen.has(o.clientId)) seen.set(o.clientId, o.clientName);
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allOrders]);

  const filteredOrders = useMemo(() => {
    const q = orderQuery.trim().toLowerCase();
    if (!q) return allOrders;
    return allOrders.filter(
      (o) =>
        o.clientName.toLowerCase().includes(q) ||
        o.id.slice(0, 8).toLowerCase().includes(q) ||
        (o.deliveryDate ?? "").includes(q),
    );
  }, [allOrders, orderQuery]);

  const toggleOrder = (id: string) => {
    setSelectedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totals = report?.totals;
  const hasReport = !!report;

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Lista de Compras Consolidada</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              Quanto comprar para suprir a demanda dos pedidos selecionados.
            </p>
          </div>
          {hasReport && totals && totals.productCount > 0 && canExport && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="gap-2"
                onClick={() => void handleExport("pdf")}
                disabled={exporting !== null}
              >
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF
              </Button>
              <Button
                variant="secondary"
                className="gap-2"
                onClick={() => void handleExport("xlsx")}
                disabled={exporting !== null}
              >
                {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                XLSX
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl border bg-card p-4 shadow-sm">
        <Tabs value={mode} onValueChange={(v) => setMode(v as Mode)}>
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="day"><CalendarRange className="mr-1.5 h-3.5 w-3.5" />Dia único</TabsTrigger>
            <TabsTrigger value="range"><CalendarRange className="mr-1.5 h-3.5 w-3.5" />Intervalo</TabsTrigger>
            <TabsTrigger value="orders"><Package className="mr-1.5 h-3.5 w-3.5" />Pedidos</TabsTrigger>
          </TabsList>

          {mode !== "orders" && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
              <span className="text-muted-foreground">Filtrar por:</span>
              <div className="inline-flex rounded-md border bg-muted p-0.5">
                <button
                  type="button"
                  onClick={() => setDateField("DELIVERY_DATE")}
                  className={`rounded px-3 py-1 font-medium transition-colors ${
                    dateField === "DELIVERY_DATE"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Data de entrega
                </button>
                <button
                  type="button"
                  onClick={() => setDateField("PURCHASE_DATE")}
                  className={`rounded px-3 py-1 font-medium transition-colors ${
                    dateField === "PURCHASE_DATE"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Data de compra
                </button>
              </div>
            </div>
          )}

          <TabsContent value="day" className="mt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">
                  {dateField === "PURCHASE_DATE" ? "Data de compra" : "Data de entrega"}
                </span>
                <DatePicker value={day} onChange={setDay} className="w-44" />
              </div>
              <Button onClick={() => void loadReport()} disabled={loadingReport} className="gap-2">
                {loadingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                Gerar lista
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="range" className="mt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">De</span>
                <DatePicker
                  value={from}
                  toDate={to || undefined}
                  onChange={(next) => {
                    setFrom(next);
                    if (next && to && next > to) setTo(next);
                  }}
                  className="w-40"
                />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-xs text-muted-foreground">Até</span>
                <DatePicker
                  value={to}
                  fromDate={from || undefined}
                  onChange={(next) => {
                    setTo(next);
                    if (next && from && next < from) setFrom(next);
                  }}
                  className="w-40"
                />
              </div>
              <Button
                onClick={() => void loadReport()}
                disabled={loadingReport || !from || !to || from > to}
                className="gap-2"
              >
                {loadingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                Gerar lista
              </Button>
              {from && to && from > to && (
                <span className="text-xs text-destructive">
                  Data inicial deve ser anterior ou igual à data final
                </span>
              )}
            </div>
          </TabsContent>

          <TabsContent value="orders" className="mt-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
                  <span className="text-xs text-muted-foreground">Buscar (cliente, pedido, data)</span>
                  <Input
                    value={orderQuery}
                    onChange={(e) => setOrderQuery(e.target.value)}
                    placeholder="Filtrar pedidos..."
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  {selectedOrderIds.size} de {allOrders.length} selecionado(s)
                </div>
                <Button onClick={() => void loadReport()} disabled={loadingReport || selectedOrderIds.size === 0} className="gap-2">
                  {loadingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
                  Gerar lista
                </Button>
              </div>

              <div className="max-h-72 overflow-y-auto rounded-md border">
                {loadingOrders ? (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : filteredOrders.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">Nenhum pedido encontrado.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Pedido</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Entrega</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredOrders.map((o) => (
                        <TableRow key={o.id}>
                          <TableCell>
                            <Checkbox checked={selectedOrderIds.has(o.id)} onCheckedChange={() => toggleOrder(o.id)} />
                          </TableCell>
                          <TableCell className="font-mono text-xs">{o.id.slice(0, 8)}</TableCell>
                          <TableCell>{o.clientName}</TableCell>
                          <TableCell>{fmtDate(o.deliveryDate)}</TableCell>
                          <TableCell>
                            <Badge variant={HANDLED_STATUSES.has(o.status) ? "secondary" : "default"}>
                              {STATUS_LABEL[o.status]}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4 grid gap-3 sm:grid-cols-[2fr_auto] border-t pt-4">
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-muted-foreground">Filtrar por cliente</Label>
            <Select value={customerFilter || "__all__"} onValueChange={(v) => setCustomerFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-9 max-w-md">
                <SelectValue placeholder="Todos os clientes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos os clientes</SelectItem>
                {uniqueCustomers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col items-start gap-1 sm:items-end">
            <Label className="text-xs text-muted-foreground">Detalhamento</Label>
            <div className="inline-flex rounded-md border bg-muted p-0.5 text-xs">
              {DETAIL_OPTIONS.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setDetailMode(value)}
                  className={`rounded px-3 py-1 font-medium transition-colors ${
                    detailMode === value
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {hasReport && totals && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <StatCard label="Pedidos" value={String(report.orderCount)} sub="incluídos" />
          <StatCard label="Produtos" value={String(totals.productCount)} sub="distintos" />
          <StatCard label="Demanda total" value={fmtQty(totals.totalDemandQty)} sub="com aproveitamento" />
          <StatCard label="Em estoque" value={fmtQty(totals.fromStockQty)} sub="já reservado" muted />
          <StatCard label="Falta comprar" value={fmtQty(totals.remainingToPurchaseQty)} sub="ação necessária" highlight />
        </div>
      )}

      {hasReport && report.orders.length > 0 && (
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <h2 className="mb-3 text-base font-semibold">Pedidos incluídos ({report.orders.length})</h2>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Entrega</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.orders.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell>{o.clientName}</TableCell>
                    <TableCell>{fmtDate(o.deliveryDate)}</TableCell>
                    <TableCell>
                      <Badge variant={HANDLED_STATUSES.has(o.status) ? "secondary" : "default"}>
                        {STATUS_LABEL[o.status]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {hasReport && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b p-4">
            <div>
              <h2 className="text-base font-semibold">Itens a comprar</h2>
              <p className="text-xs text-muted-foreground">
                Período: {fmtDate(report.from)} {report.from !== report.to ? `a ${fmtDate(report.to)}` : ""} ·
                {" "}{report.orderCount} pedido(s)
              </p>
            </div>
            {totals && totals.productCount > 0 && canExport && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2"
                  onClick={() => void handleExport("pdf")}
                  disabled={exporting !== null}
                >
                  {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                  PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-2"
                  onClick={() => void handleExport("xlsx")}
                  disabled={exporting !== null}
                >
                  {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                  XLSX
                </Button>
              </div>
            )}
          </div>
          {/* O consolidado só aparece quando NÃO está detalhando — senão a informação fica
              duplicada (cada cliente/pedido já lista seus itens abaixo). */}
          {detailMode === "none" && (report.lines.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              Nenhum item a consolidar para a seleção atual.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Unidade</TableHead>
                    <TableHead className="text-right">Solicitado (líq.)</TableHead>
                    <TableHead className="text-right">Demanda total (bruto)</TableHead>
                    <TableHead className="text-right">Em produção/entregue</TableHead>
                    <TableHead className="text-right">Já comprado</TableHead>
                    <TableHead className="text-right">Em estoque</TableHead>
                    <TableHead className="text-right font-bold">Falta comprar</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.lines.map((line) => {
                    const resolved = line.remainingToPurchaseQty <= 0;
                    return (
                      <TableRow key={`${line.productId}-${line.unit}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {line.productName}
                            {resolved && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{line.unit}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">{fmtQty(line.liquidDemandQty)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtQty(line.totalDemandQty)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {fmtQty(line.handledQty)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {fmtQty(line.alreadyPurchasedQty)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {fmtQty(line.fromStockQty)}
                        </TableCell>
                        <TableCell className={`text-right tabular-nums font-bold ${resolved ? "text-emerald-600" : "text-foreground"}`}>
                          {fmtQty(line.remainingToPurchaseQty)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}

      {hasReport && report.byCustomer && report.byCustomer.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-display text-lg font-semibold">Detalhamento por cliente</h2>
          {report.byCustomer.map((c) => (
            <div key={c.customerId} className="rounded-xl border bg-card shadow-sm">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div>
                  <div className="font-medium">{c.customerName ?? c.customerId}</div>
                  <div className="text-xs text-muted-foreground">{c.orderCount} pedido(s) · {c.lines.length} produto(s)</div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left">Produto</th>
                      <th className="px-4 py-2 text-center">Unidade</th>
                      <th className="px-4 py-2 text-right">Solicitado (líq.)</th>
                      <th className="px-4 py-2 text-right">Demanda (bruto)</th>
                      <th className="px-4 py-2 text-right">Já comprado</th>
                      <th className="px-4 py-2 text-right">Estoque</th>
                      <th className="px-4 py-2 text-right">Falta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.lines.map((line) => (
                      <tr key={`${c.customerId}-${line.productId}-${line.unit}`} className="border-t">
                        <td className="px-4 py-2">{line.productName}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{line.unit}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{fmtQty(line.liquidDemandQty)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmtQty(line.totalDemandQty)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmtQty(line.alreadyPurchasedQty)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmtQty(line.fromStockQty)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtQty(line.remainingToPurchaseQty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasReport && report.byOrder && report.byOrder.length > 0 && (
        <div className="space-y-4">
          <h2 className="font-display text-lg font-semibold">Detalhamento por pedido</h2>
          {report.byOrder.map((o) => (
            <div key={o.orderId} className="rounded-xl border bg-card shadow-sm">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div>
                  <div className="flex items-center gap-2 font-medium">
                    <span className="font-mono text-xs text-muted-foreground">#{o.shortId}</span>
                    {o.clientName ?? "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Entrega {fmtDate(o.deliveryDate)} · {o.lines.length} produto(s)
                  </div>
                </div>
                <Badge variant={HANDLED_STATUSES.has(o.status) ? "secondary" : "default"}>
                  {STATUS_LABEL[o.status]}
                </Badge>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-xs uppercase text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left">Produto</th>
                      <th className="px-4 py-2 text-center">Unidade</th>
                      <th className="px-4 py-2 text-right">Solicitado (líq.)</th>
                      <th className="px-4 py-2 text-right">Demanda (bruto)</th>
                      <th className="px-4 py-2 text-right">Já comprado</th>
                      <th className="px-4 py-2 text-right">Estoque</th>
                      <th className="px-4 py-2 text-right">Falta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.lines.map((line) => (
                      <tr key={`${o.orderId}-${line.productId}-${line.unit}`} className="border-t">
                        <td className="px-4 py-2">{line.productName}</td>
                        <td className="px-4 py-2 text-center text-muted-foreground">{line.unit}</td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">{fmtQty(line.liquidDemandQty)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmtQty(line.totalDemandQty)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmtQty(line.alreadyPurchasedQty)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmtQty(line.fromStockQty)}</td>
                        <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmtQty(line.remainingToPurchaseQty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {!hasReport && !loadingReport && (
        <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 py-16 text-center text-sm text-muted-foreground">
          <Download className="h-6 w-6" />
          Selecione um período ou pedidos e clique em <strong>Gerar lista</strong> para ver o consolidado de compras.
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  highlight,
  muted,
}: {
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
  muted?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${highlight ? "border-primary/40 bg-primary/5" : muted ? "bg-muted/40" : "bg-card"}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-xl font-bold tabular-nums ${highlight ? "text-primary" : ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}
